import { Check, Star } from "lucide-react";

/**
 * Account follow list (Wave 3i variant).
 *
 * Props:
 *  - accounts: [{id, name, status}]
 *  - selected: string[]              (account ids, in current order — index 0 is "default")
 *  - onToggle: (id) => void
 *  - onMakeDefault?: (id) => void    (optional — reorders selected so id moves to index 0)
 *  - maxSelected?: number            (default 3). When reached, unselected rows are disabled.
 *  - showDefaultBadge?: boolean      (default true)
 *  - testIdPrefix: string
 */
export default function AccountFollowList({
  accounts = [],
  selected = [],
  onToggle,
  onMakeDefault,
  maxSelected = 3,
  showDefaultBadge = true,
  testIdPrefix = "account",
}) {
  if (!accounts.length) {
    return <p className="text-sm text-zinc-900">Loading accounts…</p>;
  }

  const capReached = selected.length >= maxSelected;
  const defaultId = selected[0];

  return (
    <div className="flex flex-col gap-2" data-testid={`${testIdPrefix}-account-list`}>
      {accounts.map((acc) => {
        const isSelected = selected.includes(acc.id);
        const isLive = acc.status === "live";
        const isDefault = isSelected && acc.id === defaultId;
        const disabledByCap = !isSelected && capReached;

        // Status line copy per spec:
        //   live             → "Available now"
        //   soon + selected  → "Coming soon"
        //   soon + unselected → "Follow for launch alert"
        const statusCopy = isLive
          ? "Available now"
          : isSelected
          ? "Coming soon"
          : "Follow for launch alert";

        return (
          <div
            key={acc.id}
            className={`w-full border flex items-center gap-3 transition-all ${
              isSelected
                ? "border-amber-700 bg-amber-50 ring-1 ring-amber-700"
                : disabledByCap
                ? "border-zinc-200 bg-zinc-50 opacity-50"
                : "border-zinc-200 bg-white hover:border-zinc-300"
            }`}
            data-testid={`${testIdPrefix}-account-row-${acc.id}`}
          >
            <button
              type="button"
              onClick={() => !disabledByCap && onToggle(acc.id)}
              disabled={disabledByCap}
              aria-pressed={isSelected}
              className="flex-1 text-left p-3 flex items-center gap-3 disabled:cursor-not-allowed"
              data-testid={`${testIdPrefix}-account-${acc.id}`}
            >
              <div
                className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
                  isSelected ? "bg-amber-700 border-amber-700" : "bg-white border-zinc-300"
                }`}
              >
                {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-zinc-950">{acc.name}</span>
                  {showDefaultBadge && isDefault && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest bg-amber-700 text-white leading-none"
                      data-testid={`${testIdPrefix}-account-default-badge-${acc.id}`}
                    >
                      <Star className="w-2.5 h-2.5" strokeWidth={2.5} fill="currentColor" />
                      Default
                    </span>
                  )}
                </div>
                <span
                  className={`block mt-0.5 text-[11px] ${isLive ? "text-emerald-700" : "text-zinc-900"}`}
                  data-testid={`${testIdPrefix}-account-status-${acc.id}`}
                >
                  {statusCopy}
                </span>
              </div>
            </button>

            {/* Make default — only shown when this row is selected, not already default,
                the caller wants the handler, and the account is live. */}
            {onMakeDefault && isSelected && !isDefault && isLive && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMakeDefault(acc.id);
                }}
                className="text-[11px] uppercase tracking-widest font-medium text-amber-700 hover:text-amber-800 px-3 py-2 border-l border-amber-700/30 whitespace-nowrap"
                data-testid={`${testIdPrefix}-account-make-default-${acc.id}`}
              >
                Make default
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
